// src/hooks/useAuth.ts

import { useAuthContext } from '../contexts/AuthContext';
import type { LoginCredentials } from '../types/api.types';

/**
 * Custom hook for authentication
 * Provides a clean interface for components to interact with auth
 */

export const useAuth = () => {
  const context = useAuthContext();

  // Destructure commonly used properties and methods
  const {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    isInitialized,
    permissions,
    error,
    login,
    logout,
    forceLogout,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    getUserRole,
    canApproveCredit,
    updateUserProfile,
    clearError,
  } = context;

  // Additional computed properties for convenience
  const userDisplayName = user
    ? `${user.firstName} ${user.lastName}`.trim()
    : '';

  const userInitials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '';

  const staffInfo = user?.Staff || user?.profile;

  const userDepartment = staffInfo?.department || '';
  const userPosition = staffInfo?.position || '';
  const userRole = staffInfo?.role || '';

  // Permission shortcuts for common operations
  const permissions_shortcuts = {
    canViewMembers: hasPermission('members.read'),
    canCreateMembers: hasPermission('members.create'),
    canUpdateMembers: hasPermission('members.update'),
    canDeleteMembers: hasPermission('members.delete'),

    canViewSavings: hasPermission('savings.read'),
    canCreateSavings: hasPermission('savings.create'),
    canUpdateSavings: hasPermission('savings.update'),

    canViewCredits: hasPermission('credits.read'),
    canCreateCredits: hasPermission('credits.create'),
    canUpdateCredits: hasPermission('credits.update'),
    canApproveCredits: hasPermission('credits.approve'),
    canRejectCredits: hasPermission('credits.reject'),

    canViewReports: hasPermission('reports.read'),
    canGenerateReports: hasPermission('reports.generate'),

    canViewSettings: hasPermission('settings.read'),
    canUpdateSettings: hasPermission('settings.update'),

    // Permissions staff
    canViewStaff: hasPermission('staff.read'),
    canCreateStaff: hasPermission('staff.create'),
    canUpdateStaff: hasPermission('staff.update'),
    canDeleteStaff: hasPermission('staff.delete'),
    

    // Permissions spéciales pour les rôles
    isAdministrator: () => {
      const role = getUserRole();
      return role === 'administrator';
    },

    isAccountant: () => {
      const role = getUserRole();
      return role === 'accountant';
    },

    isCreditAgent: () => {
      const role = getUserRole();
      return role === 'credit_agent';
    },

    // Vérifier si l'utilisateur peut gérer un autre membre du staff
    canManageStaffMember: (staffMember: any) => {
      const currentUser = user;
      if (!currentUser || !staffMember) return false;

      // Les administrateurs peuvent tout gérer
      if (currentUser.Staff?.role === 'administrator') {
        // Sauf qu'on ne peut pas se gérer soi-même (pour éviter l'auto-blocage)
        if (staffMember.userId === currentUser.id) return false;
        return true;
      }

      // Les autres rôles ne peuvent pas gérer d'autres membres
      return false;
    },

    // Vérifier les limites d'approbation pour un montant donné
    canApproveAmount: (amount: number, type: 'credit' | 'savings' | 'solidarity' | 'repayment') => {
      if (!permissions?.approvalLimits) return false;
      
      const approvalLimits = permissions.approvalLimits;
      let limit: any;
      
      switch (type) {
        case 'credit':
          limit = approvalLimits.credit;
          break;
        case 'savings':
          limit = approvalLimits.savings;
          break;
        case 'solidarity':
          limit = approvalLimits.solidarity;
          break;
        case 'repayment':
          // Si repayment n'existe pas, utiliser la limite savings comme fallback
          limit = (approvalLimits as any).repayment || approvalLimits.savings;
          break;
        default:
          return false;
      }
      
      if (!limit) return false;
      if (limit === 'unlimited') return true;
      if (typeof limit === 'number') return amount <= limit;
      if (typeof limit === 'string' && !isNaN(Number(limit))) {
        return amount <= Number(limit);
      }
      return false;
    },
    
    // Obtenir le niveau d'accès de l'utilisateur
    getAccessLevel: () => {
      const role = getUserRole();
      switch (role) {
        case 'administrator': return 'full';
        case 'accountant': return 'financial';
        case 'credit_agent': return 'credit';
        default: return 'none';
      }
    },

    // Vérifier si l'utilisateur peut accéder à un module spécifique
    canAccessModule: (module: 'members' | 'staff' | 'savings' | 'credits' | 'reports' | 'settings') => {
      switch (module) {
        case 'staff':
          return hasPermission('staff.read');
        case 'members':
          return hasPermission('members.read');
        case 'savings':
          return hasPermission('savings.read');
        case 'credits':
          return hasPermission('credits.read');
        case 'reports':
          return hasPermission('reports.read');
        case 'settings':
          return hasPermission('settings.read');
        default:
          return false;
      }
    }
  };

  // Utility functions
  const requireAuth = () => {
    if (!isAuthenticated) {
      throw new Error('Authentication required');
    }
  };

  const requirePermission = (permission: string) => {
    requireAuth();
    if (!hasPermission(permission)) {
      throw new Error(`Permission required: ${permission}`);
    }
  };

  const requireAnyPermission = (permissionList: string[]) => {
    requireAuth();
    if (!hasAnyPermission(permissionList)) {
      throw new Error(`One of these permissions required: ${permissionList.join(', ')}`);
    }
  };

  const requireAllPermissions = (permissionList: string[]) => {
    requireAuth();
    if (!hasAllPermissions(permissionList)) {
      throw new Error(`All of these permissions required: ${permissionList.join(', ')}`);
    }
  };

  const requireAdmin = () => {
    requireAuth();
    if (!isAdmin()) {
      throw new Error('Administrator access required');
    }
  };

  // Login with additional error handling
  const loginUser = async (credentials: LoginCredentials) => {
    try {
      clearError(); // Clear any previous errors
      await login(credentials);
    } catch (error) {
      throw error; // Re-throw for component handling
    }
  };

  // Logout function (no confirmation needed as it's handled in header)
  const logoutUser = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error in useAuth:', error);
      throw error;
    }
  };

  // Force logout for emergency cases
  const forceLogoutUser = async () => {
    try {
      await forceLogout();
    } catch (error) {
      console.error('Force logout error in useAuth:', error);
      throw error;
    }
  };

  // Check if current session is valid
  const isSessionValid = () => {
    return isAuthenticated && user && tokens;
  };

  // Get formatted last login time
  const getLastLoginFormatted = () => {
    if (!user?.lastLoginAt) return null;

    try {
      return new Date(user.lastLoginAt).toLocaleString();
    } catch (error) {
      return null;
    }
  };

  // Get user status with color coding
  const getUserStatusInfo = () => {
    if (!user) return null;

    const statusMap = {
      active: { text: 'Active', color: '#52c41a' },
      inactive: { text: 'Inactive', color: '#faad14' },
      suspended: { text: 'Suspended', color: '#f5222d' },
    };

    return statusMap[user.status] || { text: user.status, color: '#d9d9d9' };
  };

  return {
    // Core auth state
    user,
    tokens,
    isAuthenticated,
    isLoading,
    isInitialized,
    permissions,
    error,

    // User info helpers
    userDisplayName,
    userInitials,
    userDepartment,
    userPosition,
    userRole,

    // Auth actions
    login: loginUser,
    logout: logoutUser,
    forceLogout: forceLogoutUser,
    clearError,
    updateUserProfile,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    canApproveCredit,
    getUserRole,

    // Permission shortcuts
    ...permissions_shortcuts,

    // Utility functions
    requireAuth,
    requirePermission,
    requireAnyPermission,
    requireAllPermissions,
    requireAdmin,
    isSessionValid,
    getLastLoginFormatted,
    getUserStatusInfo,

    // Raw context for advanced usage
    _context: context,
  };
};

export default useAuth;